/**
 * Query-key factory for the studies domain — the single source of truth for every
 * ["studies"] / ["study", id] / ... array read by StudiesBoard, StudyLive, StudyAuthoring,
 * StudyResults, and ArtifactChatPane's studies-side callers. Mirrors the shape of `skillsKeys`
 * (useSkills.ts) / `cloudKeys` (useCloudChat.ts).
 */
export const studiesKeys = {
  /** Prefix of every studies board query (invalidate this after a create/move/archive). */
  list: ["studies"] as const,
  /** A member's board is scoped to their current org: switching org can't flash the old board. */
  board: (orgId: string | null) => ["studies", "board", orgId] as const,
  detail: (id: string) => ["study", id] as const,
  settings: (id: string) => ["study-settings", id] as const,
  followers: (id: string) => ["study-followers", id] as const,
  shares: (id: string) => ["study-shares", id] as const,
  emails: (id: string) => ["study-emails", id] as const,
  scope: (id: string) => ["study-scope", id] as const,
  codingMatrix: (id: string) => ["study-coding-matrix", id] as const,
  codingAnswers: (id: string) => ["study-coding-answers", id] as const,
  codingHypotheses: (id: string) => ["study-coding-hypotheses", id] as const,
  codingSummary: (id: string) => ["study-coding-summary", id] as const,
  hypotheses: (id: string) => ["study-hypotheses", id] as const,
  overview: (id: string) => ["study-legacy-overview", id] as const,
  responseGroups: (id: string) => ["study-response-groups", id] as const,
  report: (id: string) => ["study-report", id] as const,
  /** The analysis' web sources. */
  sources: (id: string) => ["study-sources", id] as const,
  /** The study's own sources: files people and agents add (the Sources tab, keyed `documents`). */
  studySources: (id: string) => ["study-documents", id] as const,
  exhibits: (id: string) => ["study-exhibits", id] as const,
  conversations: (id: string) => ["study-conversations", id] as const,
  intake: (id: string) => ["study-intake", id] as const,
  distributions: (studyId: string) => ["distributions", studyId] as const,
  /** Experts ledger: the roster (prefix for every filter) and one expert's detail. */
  experts: (studyId: string) => ["study-experts", studyId] as const,
  /** The client's view: published experts, numbered instead of named. */
  clientExperts: (studyId: string) => ["client-experts", studyId] as const,
  expertList: (studyId: string, filters: string) => ["study-experts", studyId, "list", filters] as const,
  expert: (studyId: string, expertId: string) => ["study-experts", studyId, "detail", expertId] as const,
};
